import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ArrowLeft, FileText, Lock, X, CheckCircle, AlertCircle, Key } from 'lucide-react';
import { toast } from 'sonner';
import { useEmbeddedMode, postEmbeddedStepComplete } from '../hooks/useEmbeddedMode';

// Agreement configuration by role and state
// iter166 — Exported so HomePage can compute the user's role/state-
// specific access password without duplicating the table.
export const AGREEMENT_CONFIG = {
  affiliate: {
    default: {
      passwordA: 'Affiliate2222',
      link: 'https://www.collectiveorb.com/affiliate-equity-agreement',
      passwordB: 'Orb2026AAll'
    }
  },
  lead: {
    'North Carolina': {
      passwordA: 'NCRegion1234',
      link: 'https://www.collectiveorb.com/affiliate-equity-agreement',
      passwordB: 'Orb2026LNC'
    },
    'South Carolina': {
      passwordA: 'NCRegion1234',
      link: 'https://www.collectiveorb.com/affiliate-equity-agreement',
      passwordB: 'Orb2026LNC'
    },
    'California': {
      passwordA: 'CARegion9101',
      link: 'https://www.collectiveorb.com/ca-agreement',
      passwordB: 'Orb2026LCA'
    },
    'New York': {
      passwordA: 'NYRegion1212',
      link: 'https://www.collectiveorb.com/ny-lead-agreement-signing',
      passwordB: 'Orb2026LNY'
    },
    'District of Columbia': {
      passwordA: 'DCRegion3434',
      link: 'https://www.collectiveorb.com/dc-va-agreement',
      passwordB: 'Orb2026LDC'
    },
    'Virginia': {
      passwordA: 'DCRegion3434',
      link: 'https://www.collectiveorb.com/dc-va-agreement',
      passwordB: 'Orb2026LDC'
    },
    'Massachusetts': {
      passwordA: 'MARegion7878',
      link: 'https://www.collectiveorb.com/ma-agreement',
      passwordB: 'Orb2026LMA'
    },
    'Illinois': {
      passwordA: 'ILRegion5656',
      link: 'https://www.collectiveorb.com/il-agreement',
      passwordB: 'Orb2026LIL'
    },
    'Pennsylvania': {
      passwordA: 'PAMDRegion2323',
      link: 'https://www.collectiveorb.com/pa-md-agreement',
      passwordB: 'Orb2026LPA'
    },
    'Maryland': {
      passwordA: 'PAMDRegion2323',
      link: 'https://www.collectiveorb.com/pa-md-agreement',
      passwordB: 'Orb2026LPA'
    },
    'Georgia': {
      passwordA: 'GARegion1414',
      link: 'https://www.collectiveorb.com/ga-agreement',
      passwordB: 'Orb2026LGA'
    },
    'Florida': {
      passwordA: 'FLRegion9090',
      link: 'https://www.collectiveorb.com/fl-agreement',
      passwordB: 'Orb2026LFL'
    },
    'Michigan': {
      passwordA: 'MORRegion6767',
      link: 'https://www.collectiveorb.com/mi-oh-agreement',
      passwordB: 'Orb2026LMI'
    },
    'Ohio': {
      passwordA: 'MORRegion6767',
      link: 'https://www.collectiveorb.com/mi-oh-agreement',
      passwordB: 'Orb2026LMI'
    },
    'Tennessee': {
      passwordA: 'TKRegion8383',
      link: 'https://www.collectiveorb.com/tn-ky-agreement',
      passwordB: 'Orb2026LTN'
    },
    'Kentucky': {
      passwordA: 'TKRegion8383',
      link: 'https://www.collectiveorb.com/tn-ky-agreement',
      passwordB: 'Orb2026LTN'
    },
    'Washington': {
      passwordA: 'WORRegion4747',
      link: 'https://www.collectiveorb.com/wa-or-agreement',
      passwordB: 'Orb2026LWA'
    },
    'Oregon': {
      passwordA: 'WORRegion4747',
      link: 'https://www.collectiveorb.com/wa-or-agreement',
      passwordB: 'Orb2026LWA'
    },
    default: {
      passwordA: 'EALeads1940',
      link: 'https://www.collectiveorb.com/ea-lead-agreement',
      passwordB: 'Orb2026LAll'
    }
  },
  supervisor: {
    default: {
      passwordA: 'ORB26542Go!@',
      link: 'https://www.collectiveorb.com/supervisor-agreement',
      passwordB: 'Orb2026LSAll'
    }
  }
};

// iter166 — Pure helper exported for use anywhere that needs the
// user's agreement config (e.g. HomePage shows the access password
// on the Step 3 card).
export const getAgreementConfigFor = (role, state) => {
  const r = (role || '').toLowerCase();
  if (!r || r === 'admin') return null;
  const roleConfig = AGREEMENT_CONFIG[r];
  if (!roleConfig) return AGREEMENT_CONFIG.affiliate.default;
  if (r === 'affiliate' || r === 'supervisor') return roleConfig.default;
  if (r === 'lead' && state && roleConfig[state]) return roleConfig[state];
  return roleConfig.default;
};

const AgreementSigningPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, api, refreshUser } = useAuth();
  const embedded = useEmbeddedMode();
  const [showAgreementForm, setShowAgreementForm] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [confirmationCode, setConfirmationCode] = useState('');
  const [verifying, setVerifying] = useState(false);

  // Check if came from admin onboarding - check multiple sources
  const fromAdmin = location.state?.fromAdmin || 
    sessionStorage.getItem('admin_onboarding_return') === 'true' ||
    document.referrer.includes('/admin/onboarding');

  // Handle back navigation
  const handleBack = () => {
    sessionStorage.removeItem('admin_onboarding_return');
    if (fromAdmin) {
      navigate('/admin/onboarding');
    } else {
      navigate('/');
    }
  };

  // Check if already completed
  const hasCompleted = user?.agreement_signed || user?.is_onboarded || user?.step3_agreement_signed;

  // Get agreement config based on user role and state
  const getAgreementConfig = () => {
    const role = user?.role?.toLowerCase();
    const state = user?.state;

    if (!role || role === 'admin') {
      return null;
    }

    const roleConfig = AGREEMENT_CONFIG[role];
    if (!roleConfig) {
      return AGREEMENT_CONFIG.affiliate.default;
    }

    if (role === 'affiliate' || role === 'supervisor') {
      return roleConfig.default;
    }

    if (role === 'lead' && state && roleConfig[state]) {
      return roleConfig[state];
    }

    return roleConfig.default;
  };

  const config = getAgreementConfig();

  const handleOpenAgreement = () => {
    setShowAgreementForm(true);
  };

  const handleExitForm = () => {
    setShowAgreementForm(false);
    setShowPasswordDialog(true);
  };

  const handleVerify = async () => {
    if (!confirmationCode.trim()) {
      toast.error('Please enter the confirmation code');
      return;
    }

    setVerifying(true);
    try {
      await api.post('/agreement/verify', {
        confirmation_code: confirmationCode.trim()
      });
      
      await refreshUser();
      toast.success('Agreement signed! You are now fully onboarded!');
      setShowPasswordDialog(false);
      if (embedded) {
        postEmbeddedStepComplete('agreement_signed');
      } else {
        navigate('/');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid confirmation code. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  const handleClosePasswordDialog = () => {
    setShowPasswordDialog(false);
    setConfirmationCode('');
  };

  // Admin or completed state
  if (user?.role === 'admin' || hasCompleted) {
    return (
      <div className={embedded ? "p-2" : "min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] p-4"}>
        <div className="max-w-md mx-auto pt-6">
          <div className="flex items-center gap-4 mb-6">
            <Button variant="ghost" size="icon" onClick={handleBack} className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-2xl font-bold">Sign Agreement</h1>
          </div>
          
          <Card className="bg-white/90 dark:bg-[#141414] border-0 shadow-lg">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-12 h-12 text-green-600 dark:text-green-400" />
              </div>
              <h2 className="text-2xl font-bold mb-2">Agreement Signed</h2>
              <p className="text-muted-foreground mb-6">
                {user?.role === 'admin' 
                  ? 'As an admin, you have full access to the platform.'
                  : 'You have completed all onboarding steps. Welcome aboard!'}
              </p>
              <Button onClick={() => navigate('/')} className="w-full">
                Go to Dashboard
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className={embedded ? "p-2" : "min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] p-4"}>
        <div className="max-w-md mx-auto pt-6">
          <Card className="bg-white/90 dark:bg-[#141414] border-0 shadow-lg">
            <CardContent className="p-8 text-center">
              <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold mb-2">Configuration Error</h2>
              <p className="text-muted-foreground mb-4">
                Unable to load agreement configuration. Please contact support.
              </p>
              <Button onClick={() => navigate('/')}>Return to Home</Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // iter166 — Embedded popup branch must run BEFORE the standalone
  // showAgreementForm branch below — otherwise the popup user gets
  // the standalone fullscreen iframe header. Inside embedded mode,
  // the iframe renders bare (no header chrome): the popup's own
  // drag handle + close X are the only chrome.
  if (embedded) {
    if (showAgreementForm) {
      return (
        <div className="fixed inset-0 bg-white flex flex-col">
          <iframe
            src={config.link}
            className="w-full h-full flex-1 border-0"
            title="Agreement Form"
            allow="camera; microphone"
          />
        </div>
      );
    }

    return (
      <div className="min-h-screen flex flex-col bg-white text-[#1c1a1c]">
        <div className="px-6 pt-8 pb-4 flex flex-col items-center text-center">
          <div className="w-16 h-16 bg-[#e8ede6] rounded-2xl flex items-center justify-center mb-4 shadow-sm">
            <FileText className="w-8 h-8 text-[#757150]" />
          </div>
          <h2 className="text-2xl font-bold text-[#1c1a1c]">
            {user?.role?.charAt(0).toUpperCase() + user?.role?.slice(1)} Agreement
          </h2>
        </div>

        <div className="px-6 flex-1 flex flex-col justify-center">
          <div className="rounded-2xl bg-[#fafaf7] border border-[#ece9df] p-5 shadow-sm">
            <p className="text-[14.5px] text-[#3a3a3a] leading-[1.6]">
              To complete your onboarding, you need to sign the{' '}
              <span className="font-semibold text-[#1c1a1c]">{user?.role} Agreement</span>. This
              is the final step before you gain full access to the platform.
            </p>
          </div>

          <div className="mt-4 rounded-2xl bg-[#f5f5f0] border-l-[3px] border-[#757150] px-4 py-3.5 flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center flex-shrink-0 shadow-sm">
              <Key className="w-4 h-4 text-[#757150]" />
            </div>
            <div className="flex-1">
              <p className="text-[13.5px] font-semibold text-[#1c1a1c] mb-0.5">After signing</p>
              <p className="text-[12.5px] text-[#5a5a5a] leading-[1.5]">
                You'll receive a <span className="font-semibold text-[#1c1a1c]">confirmation code</span>.
                Return to the home page to enter the code and complete onboarding.
              </p>
            </div>
          </div>
        </div>

        <div className="px-6 pt-4 pb-6 bg-white" style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}>
          <Button
            className="w-full bg-[#757150] hover:bg-[#5c5840] text-white h-12 rounded-xl flex items-center justify-center gap-2 text-base shadow-sm"
            onClick={handleOpenAgreement}
            data-testid="sign-agreement-btn"
          >
            <FileText className="w-4 h-4" />
            Sign the Agreement
          </Button>
        </div>
      </div>
    );
  }

  // Full Screen Agreement Form (Iframe) — STANDALONE /sign-agreement
  // route only. The embedded popup version above renders a bare
  // iframe without this header chrome.
  if (showAgreementForm) {
    return (
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#ffffff',
          overflow: 'hidden'
        }}
      >
        {/* Header */}
        <div 
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 16px',
            borderBottom: '1px solid #dfdfdf',
            backgroundColor: '#ffffff',
            flexShrink: 0
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <FileText style={{ width: '20px', height: '20px', color: '#757150' }} />
            <div>
              <span style={{ fontWeight: 600, color: '#1c1a1c' }}>
                {user?.role?.charAt(0).toUpperCase() + user?.role?.slice(1)} Agreement
              </span>
              <p style={{ fontSize: '12px', color: '#676767', margin: 0 }}>
                Password: <code style={{ backgroundColor: '#f0f0f0', padding: '2px 4px', borderRadius: '4px' }}>{config.passwordA}</code>
              </p>
            </div>
          </div>
          <Button 
            variant="outline"
            onClick={handleExitForm}
            className="gap-2"
            data-testid="exit-agreement-form-btn"
          >
            <X className="w-4 h-4" />
            Exit & Enter Code
          </Button>
        </div>
        
        {/* Iframe Container - Zoomed to fill screen perfectly */}
        <div style={{ 
          flex: 1, 
          overflow: 'hidden',
          position: 'relative'
        }}>
          <iframe
            src={config.link}
            title="Agreement Form"
            allow="camera; microphone"
            scrolling="yes"
            style={{
              width: '73.5%',
              height: '73.5%',
              border: 'none',
              display: 'block',
              transform: 'scale(1.36)',
              transformOrigin: 'top left',
              position: 'absolute',
              top: 0,
              left: 0
            }}
          />
        </div>
      </div>
    );
  }

  // Main Agreement Page — standalone /sign-agreement route (unchanged).
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] p-4 pb-24" data-testid="agreement-signing-page">
      <div className="max-w-md mx-auto pt-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={handleBack}
            className="rounded-full"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-[#1c1a1c]">Sign Agreement</h1>
            <p className="text-sm text-[#676767]">Step 3: Final Step</p>
          </div>
        </div>

        {/* Agreement Info Card */}
        <Card className="bg-white/90 dark:bg-[#141414] border-0 shadow-lg mb-4">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#e8ede6] dark:bg-[#2a2a2a] rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-[#757150]" />
              </div>
              <h2 className="text-xl font-bold text-[#1c1a1c]">
                {user?.role?.charAt(0).toUpperCase() + user?.role?.slice(1)} Agreement
              </h2>
            </div>

            <div className="space-y-4">
              <p className="text-[#676767]">
                To complete your onboarding, you need to sign the <span className="font-semibold text-[#1c1a1c]">{user?.role} Agreement</span>. 
                This is the final step before you gain full access to the platform.
              </p>

              {/* Access Password Info */}
              <div className="bg-[#f5f5f5] dark:bg-[#1a1a1a] rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Lock className="w-5 h-5 text-[#757150] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-[#1c1a1c] mb-1">Access Password</p>
                    <p className="text-sm text-[#676767] mb-2">
                      When prompted, use this password to access the agreement:
                    </p>
                    <div className="bg-white dark:bg-[#2a2a2a] rounded p-2">
                      <code className="font-mono text-primary font-semibold">{config.passwordA}</code>
                    </div>
                  </div>
                </div>
              </div>

              {/* Confirmation Code Info */}
              <div className="bg-[#f5f5f5] dark:bg-[#1a1a1a] rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Key className="w-5 h-5 text-[#757150] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-[#1c1a1c] mb-1">After Signing</p>
                    <p className="text-sm text-[#676767]">
                      After signing, you will receive a <span className="font-semibold">confirmation code</span>. 
                      Return here and enter that code to complete your onboarding.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Sign Agreement Button */}
        <Button 
          className="w-full bg-[#2C3E50] hover:bg-[#34495E] text-white h-12 flex items-center justify-center gap-2"
          onClick={handleOpenAgreement}
          data-testid="sign-agreement-btn"
        >
          <FileText className="w-4 h-4" />
          Sign the Agreement
        </Button>

        {/* Already signed link */}
        <button 
          onClick={() => setShowPasswordDialog(true)}
          className="w-full text-center mt-3 text-sm text-[#676767] hover:text-[#1c1a1c] underline"
          data-testid="already-signed-btn"
        >
          I've already signed - Enter confirmation code
        </button>

        {/* Back link */}
        <button 
          onClick={() => navigate('/')}
          className="w-full text-center mt-2 text-sm text-[#676767] hover:text-[#1c1a1c]"
        >
          I'll do this later
        </button>
      </div>

      {/* Confirmation Code Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={handleClosePasswordDialog}>
        <DialogContent className="max-w-sm mx-auto bg-white dark:bg-[#1a1a1a]">
          <DialogHeader>
            <DialogTitle className="text-xl">Enter Confirmation Code</DialogTitle>
            <p className="text-sm text-[#676767]">
              Enter the confirmation code you received after signing the agreement.
            </p>
          </DialogHeader>

          <div className="space-y-4">
            <div className="bg-[#f5f5f5] dark:bg-[#2a2a2a] rounded-lg p-4">
              <div className="flex items-start gap-2 mb-3">
                <AlertCircle className="w-4 h-4 text-[#757150] flex-shrink-0 mt-0.5" />
                <p className="text-xs text-[#676767]">
                  If you haven't signed the agreement yet, click "Sign the Agreement" button first. 
                  The confirmation code will be provided after you complete signing.
                </p>
              </div>
              
              <Label className="text-sm text-[#676767]">
                Confirmation Code
              </Label>
              <Input
                type="text"
                placeholder="Enter confirmation code"
                value={confirmationCode}
                onChange={(e) => setConfirmationCode(e.target.value)}
                className="mt-2"
                data-testid="confirmation-code-input"
              />
            </div>

            <div className="flex gap-2">
              <Button 
                variant="outline"
                className="flex-1"
                onClick={handleClosePasswordDialog}
              >
                Cancel
              </Button>
              <Button 
                className="flex-1 bg-[#2C3E50] hover:bg-[#34495E] text-white"
                onClick={handleVerify}
                disabled={verifying || !confirmationCode.trim()}
                data-testid="verify-code-btn"
              >
                {verifying ? 'Verifying...' : 'Verify & Complete'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AgreementSigningPage;
